import { Checkbox, FormControlLabel } from '@mui/material';
import Grid from '@mui/material/Grid2';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSolidInput from 'components/AcSolidInput/AcSolidInput';
import RichText from 'components/RichText/RichText';
import Textarea from 'components/Textarea/Textarea';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import UploadImage from 'components/UploadImage/UploadImage';

import { PortalSettingsProps } from '../types';

import {
    DEFAULT_VALUES,
    FORM_LABELS,
    UPLOAD_CONFIG
} from './constants'

import './style.scss';

const PortalSettings = ({ control, errors, watch }: PortalSettingsProps) => {
  // Watch the active checkbox value to conditionally show required fields
  const isActive = watch('settings.addToHomeScreen.active');

  return (
    <>
      {/* ----- SEO Settings Section ----- */}
      <div className="seo-settings__container">
        <RichText
          title="SEO Settings"
          description="Customize how this page appears in search results and social media previews."
          className="mb-9"
          data-testid="PortalSettings-SEO-Title"
        />
        <div className="grid grid-cols-1 gap-y-9 w-[586px]">
          <FormField
            name="settings.seo.portalTitle"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <AcInput
                    header="SEO title"
                    placeholder="Add a title"
                    value={value}
                    onChange={onChange}
                    data-testid="PortalSettings-Title"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="settings.seo.portalDescription"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    label="Description"
                    data-testid="PortalSettings-Description"
                    placeholder="Write a short summary that describes this page for search engines."
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  className="mt-1 text-p-s text-base-destructive"
                  data-testid="PortalSettings-SEO-TitleError"
                />
              </FormItem>
            )}
          />
          <FormField
            name="settings.seo.portalKeywords"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <AcInput
                    header="Keywords"
                    placeholder="Enter keywords, separated by commas"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            onChange(e.target.value.split(',').map((v: string) => v.trim()))
                    }
                    value={value && value?.length > 0 ? value.join(', ') : ''}
                    data-testid="PortalSettings-Keywords"
                  />
                </FormControl>
                <FormMessage className="mt-1 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
          <FormField
            name="settings.seo.portalOGImage"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <UploadImage
                    buttonDataTestId="PortalSettings-SEOImageUploadBtn"
                    className="w-full"
                    title="SEO / Social Share Image"
                    description={`JPG, PNG, WebP — recommended size 1200x630px`}
                    buttonText="Upload"
                    selectedImageUrl={value}
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* ----- Add To Home Screen Button Section ----- */}
      <div className="add-to-home-screen__container">
                <AcCard stackContainer={false} title="Add To Home Screen Button" maxWidth={586}>
                    <Grid className="add-to-home-screen__grid" container rowSpacing={2.5} columnSpacing={1.5}>
            <Grid size={12}>
              <FormField
                control={control}
                name="settings.addToHomeScreen.active"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <FormControlLabel
                        className="add-to-home-screen__form-label"
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            size="small"
                            data-testid="PortalSettings-ActivateFeatureCheckbox"
                            className="add-to-home-screen__checkbox"
                          />
                        }
                        label={FORM_LABELS.ACTIVATE_FEATURE}
                        data-testid="PortalSettings-ActivateFeature"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </Grid>

            <Grid size={6}>
              <FormField
                control={control}
                name="settings.addToHomeScreen.borderColor"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <AcSolidInput
                        header={FORM_LABELS.BORDER_AND_TEXT_COLOR}
                        defaultValue={value || DEFAULT_VALUES.BORDER_COLOR}
                        setValue={(_: unknown, color: string) => {
                          onChange(color);
                        }}
                        data-testid="PortalSettings-BorderColor"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </Grid>

            <Grid size={6}>
              <FormField
                control={control}
                name="settings.addToHomeScreen.backgroundColor"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <AcSolidInput
                        header={FORM_LABELS.BACKGROUND_COLOR}
                        defaultValue={value || DEFAULT_VALUES.BACKGROUND_COLOR}
                        setValue={(_: unknown, color: string) => {
                          onChange(color);
                        }}
                        data-testid="PortalSettings-BackgroundColor"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </Grid>

            <Grid size={12}>
              <FormField
                control={control}
                name="settings.addToHomeScreen.buttonImage"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <div className="mt-4">
                        <UploadImage
                          buttonDataTestId="PortalSettings-ButtonImageUploadBtn" 
                          className="w-full button-image-container"
                          title={FORM_LABELS.BUTTON_IMAGE}
                          description={UPLOAD_CONFIG.BUTTON_IMAGE_DESCRIPTION}
                          buttonText={UPLOAD_CONFIG.BUTTON_TEXT}
                          selectedImageUrl={value}
                          onUpload={onChange}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </Grid>

            <Grid size={12}>
              <FormField
                control={control}
                name="settings.addToHomeScreen.iconImage"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <div className="mt-4">
                        <UploadImage
                          buttonDataTestId="PortalSettings-IconImageUploadBtn"
                          isRequired={isActive}
                          className="w-full icon-image-container"
                          title={FORM_LABELS.ICON_IMAGE}
                          description={UPLOAD_CONFIG.ICON_IMAGE_TOOLTIP}
                          buttonText={UPLOAD_CONFIG.BUTTON_TEXT}
                          selectedImageUrl={value}
                          onUpload={onChange}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </Grid>

            <Grid size={12}>
              <FormField
                control={control}
                name="settings.addToHomeScreen.shortcutName"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <AcInput
                        required={isActive}
                        header={FORM_LABELS.PORTAL_TITLE}
                        value={value}
                        type="text"
                        tooltip="Will be the name of your home screen icon"
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={FORM_LABELS.PORTAL_TITLE}
                        error={!!errors.settings?.addToHomeScreen?.shortcutName}
                        helperText={
                          errors.settings?.addToHomeScreen?.shortcutName
                            ?.message
                        }
                        dataTestId="PortalSettings-ShortcutName"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </Grid>
          </Grid>
        </AcCard>
      </div>
    </>
  );
};

export default PortalSettings;
